import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import { useToast } from '../../components/ToastProvider';
import TransactionRecorder from './TransactionRecorder';

const InventoryReports = () => {
    const addToast = useToast();
    const [stockReport, setStockReport] = useState(null);
    const [costAnalysis, setCostAnalysis] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [activeTab, setActiveTab] = useState('stock');
    const [showTransactionRecorder, setShowTransactionRecorder] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        fetchStockReport();
        fetchCostAnalysis();
        fetchTransactions();
    }, []);

    const fetchStockReport = async () => {
        try {
            const { restaurant } = JSON.parse(localStorage.getItem('restaurantData') || '{}');
            const restaurantId = restaurant?._id || JSON.parse(localStorage.getItem('restaurantData'))?._id;
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_STOCK_REPORT}?restaurantId=${restaurantId}`);
            setStockReport(response.data);
        } catch (error) {
            console.error('Error fetching stock report:', error);
        }
    };

    const fetchCostAnalysis = async () => {
        try {
            const { restaurant } = JSON.parse(localStorage.getItem('restaurantData') || '{}');
            const restaurantId = restaurant?._id || JSON.parse(localStorage.getItem('restaurantData'))?._id;
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_COST_ANALYSIS}?restaurantId=${restaurantId}`);
            setCostAnalysis(response.data);
        } catch (error) {
            console.error('Error fetching cost analysis:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const { restaurant } = JSON.parse(localStorage.getItem('restaurantData') || '{}');
            const restaurantId = restaurant?._id || JSON.parse(localStorage.getItem('restaurantData'))?._id;
            const response = await axios.get(API_ENDPOINTS.INVENTORY_TRANSACTIONS, {
                params: {
                    ...dateRange,
                    restaurantId
                }
            });
            setTransactions(response.data);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const applyDateFilter = () => {
        fetchTransactions();
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                fetchStockReport(),
                fetchCostAnalysis(),
                fetchTransactions()
            ]);
            addToast('Data refreshed successfully!', 'success');
        } catch (error) {
            console.error('Error refreshing data:', error);
            addToast('Failed to refresh data', 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    const renderStockReport = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white border border-stone-200 p-3 rounded-xl shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Total Ingredients</p>
                    <p className="text-xl font-bold text-orange-600 mt-0.5">{stockReport?.totalIngredients || 0}</p>
                </div>
                <div className="bg-white border border-red-200 p-3 rounded-xl shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Low Stock</p>
                    <p className="text-xl font-bold text-red-600 mt-0.5">{stockReport?.lowStockItems || 0}</p>
                </div>
                <div className="bg-white border border-stone-200 p-3 rounded-xl shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">In Stock</p>
                    <p className="text-xl font-bold text-emerald-600 mt-0.5">
                        {(stockReport?.totalIngredients || 0) - (stockReport?.lowStockItems || 0)}
                    </p>
                </div>
            </div>

            {stockReport?.lowStockAlerts?.length > 0 && (
                <div className="bg-red-50/80 border border-red-200 p-3 rounded-xl">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-red-700 mb-2">Low Stock Alerts</p>
                    <div className="space-y-1.5">
                        {stockReport.lowStockAlerts.map((item) => (
                            <div key={item._id} className="flex justify-between items-center bg-white px-2.5 py-2 rounded-lg border border-red-100 text-xs">
                                <span className="font-medium text-stone-800">{item.name}</span>
                                <span className="text-stone-600">
                                    Current: {item.currentStock} {item.unit} · Reorder: {item.reorderLevel} {item.unit}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 py-2 border-b border-stone-200 bg-stone-50">All Ingredients Stock Status</p>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] text-xs">
                        <thead className="bg-stone-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Ingredient</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Current</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Reorder</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Unit Price</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Value</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stockReport?.ingredients?.map((ingredient) => (
                                <tr key={ingredient._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                    <td className="px-3 py-2 font-medium text-stone-800">{ingredient.name}</td>
                                    <td className="px-3 py-2 text-stone-600">{ingredient.currentStock?.toFixed(2)} {ingredient.unit}</td>
                                    <td className="px-3 py-2 text-stone-600">{ingredient.reorderLevel?.toFixed(2)} {ingredient.unit}</td>
                                    <td className="px-3 py-2 tabular-nums">₹{ingredient.purchasePrice?.toFixed(2)}</td>
                                    <td className="px-3 py-2 tabular-nums">₹{(ingredient.currentStock * ingredient.purchasePrice)?.toFixed(2)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            ingredient.currentStock <= ingredient.reorderLevel
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                            {ingredient.currentStock <= ingredient.reorderLevel ? 'Low' : 'OK'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderCostAnalysis = () => (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 py-2 border-b border-stone-200 bg-stone-50">Menu Item Profitability</p>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-xs">
                    <thead className="bg-stone-50">
                        <tr>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">Menu Item</th>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">Cost</th>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">Selling</th>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">Profit</th>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">%</th>
                            <th className="px-3 py-2 text-left font-semibold text-stone-600">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {costAnalysis.map((item, index) => (
                            <tr key={index} className="border-t border-stone-100 hover:bg-stone-50/50">
                                <td className="px-3 py-2 font-medium text-stone-800">{item.menuItem}</td>
                                <td className="px-3 py-2 tabular-nums">₹{item.costPrice?.toFixed(2)}</td>
                                <td className="px-3 py-2 tabular-nums">₹{item.sellingPrice}</td>
                                <td className="px-3 py-2 tabular-nums text-orange-600">₹{item.profitAmount?.toFixed(2)}</td>
                                <td className="px-3 py-2 tabular-nums">{item.profitPercentage}%</td>
                                <td className="px-3 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        item.profitPercentage > 30 ? 'bg-emerald-100 text-emerald-800' :
                                        item.profitPercentage > 15 ? 'bg-amber-100 text-amber-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {item.profitPercentage > 30 ? 'High' : item.profitPercentage > 15 ? 'Moderate' : 'Low'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderTransactions = () => (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-2">
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Start</label>
                    <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                        className="border border-stone-200 px-2.5 py-2 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">End</label>
                    <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                        className="border border-stone-200 px-2.5 py-2 rounded-lg text-xs focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                    />
                </div>
                <button
                    type="button"
                    onClick={applyDateFilter}
                    className="px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                >
                    Apply
                </button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] text-xs">
                        <thead className="bg-stone-50">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Date</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Ingredient</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Type</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Qty</th>
                                <th className="px-3 py-2 text-left font-semibold text-stone-600">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((transaction) => (
                                <tr key={transaction._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                    <td className="px-3 py-2 text-stone-600">
                                        {new Date(transaction.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-3 py-2 font-medium text-stone-800">{transaction.ingredientId?.name}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            transaction.transactionType === 'consumption' ? 'bg-red-100 text-red-800' :
                                            transaction.transactionType === 'purchase' ? 'bg-emerald-100 text-emerald-800' :
                                            transaction.transactionType === 'wastage' ? 'bg-orange-100 text-orange-800' :
                                            'bg-stone-200 text-stone-700'
                                        }`}>
                                            {transaction.transactionType}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {transaction.quantity} {transaction.ingredientId?.unit}
                                    </td>
                                    <td className="px-3 py-2 text-stone-600">{transaction.notes || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800">Reports</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {isRefreshing ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowTransactionRecorder(true)}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    >
                        <span className="text-base leading-none">＋</span>
                        Record Transaction
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { id: 'stock', label: 'Stock Report' },
                    { id: 'cost', label: 'Cost Analysis' },
                    { id: 'transactions', label: 'Transactions' }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                            activeTab === tab.id
                                ? 'bg-orange-500 text-white shadow-md'
                                : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'stock' && renderStockReport()}
            {activeTab === 'cost' && renderCostAnalysis()}
            {activeTab === 'transactions' && renderTransactions()}

            {showTransactionRecorder && (
                <TransactionRecorder
                    onClose={() => setShowTransactionRecorder(false)}
                    onTransactionRecorded={() => {
                        fetchStockReport();
                        fetchTransactions();
                    }}
                />
            )}
        </div>
    );
};

export default InventoryReports;